import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { EnvelopeSimple, Clock, CaretDown } from "@phosphor-icons/react";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";

const STATUSES = [
    { value: "aperta", label: "Aperta", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    { value: "in_lavorazione", label: "In lavorazione", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    { value: "evasa", label: "Evasa", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    { value: "chiusa", label: "Chiusa", cls: "bg-gray-100 text-gray-600 border-gray-200" },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.value, s]));

const TYPE_LABEL = {
    assistenza: "Assistenza", accesso: "Accesso (art.15)", rettifica: "Rettifica (art.16)",
    cancellazione: "Cancellazione (art.17)", portabilita: "Portabilità (art.20)",
    opposizione: "Opposizione (art.21)", limitazione: "Limitazione (art.18)", altro: "Altro",
};

function StatusBadge({ status }) {
    const s = STATUS_MAP[status] || STATUS_MAP.aperta;
    return <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${s.cls}`}>{s.label}</span>;
}

export default function GdprRequests() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("all");
    const [openId, setOpenId] = useState(null);
    const [draft, setDraft] = useState({ status: "", note: "" });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const params = filter !== "all" ? { status: filter } : {};
            const [r, s] = await Promise.all([
                api.get("/admin/gdpr/requests", { params }),
                api.get("/admin/gdpr/stats"),
            ]);
            setRows(r.data);
            setStats(s.data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    }, [filter]);

    useEffect(() => {
        if (user && user.role === "admin") load();
    }, [user, load]);

    if (user === null) return null;
    if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

    const openDetail = (req) => {
        if (openId === req.id) { setOpenId(null); return; }
        setOpenId(req.id);
        setDraft({ status: req.status, note: "" });
    };

    const saveStatus = async (id) => {
        if (!draft.status) return;
        setSaving(true);
        try {
            await api.patch(`/admin/gdpr/requests/${id}`, draft);
            toast.success("Richiesta aggiornata");
            setOpenId(null);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Layout>
            <div data-testid="gdpr-admin-page">
                <div className="overline mb-2">Admin · Compliance</div>
                <h1 className="font-display-black text-4xl mb-2 leading-tight">Richieste GDPR &amp; Contatti</h1>
                <p className="text-sm text-gray-600 mb-8">Gestisci le richieste degli interessati con stato e cronologia.</p>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    {STATUSES.map((s) => (
                        <button
                            key={s.value}
                            onClick={() => setFilter(filter === s.value ? "all" : s.value)}
                            data-testid={`gdpr-stat-${s.value}`}
                            className={`text-left border p-4 transition-colors ${filter === s.value ? "border-black" : "border-gray-200 hover:border-gray-400"}`}
                        >
                            <div className="font-display-black text-3xl">{stats ? (stats[s.value] ?? 0) : "—"}</div>
                            <div className="overline mt-1">{s.label}</div>
                        </button>
                    ))}
                </div>

                <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-gray-500">{rows.length} richieste {filter !== "all" ? `· filtro: ${STATUS_MAP[filter].label}` : ""}</div>
                    {filter !== "all" && (
                        <button onClick={() => setFilter("all")} data-testid="gdpr-clear-filter" className="text-xs font-semibold text-gray-500 hover:text-black underline">Mostra tutte</button>
                    )}
                </div>

                {/* List */}
                <div className="space-y-2" data-testid="gdpr-request-list">
                    {rows.length === 0 && (
                        <div className="border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">Nessuna richiesta.</div>
                    )}
                    {rows.map((req) => (
                        <div key={req.id} className="border border-gray-200 bg-white" data-testid={`gdpr-request-${req.id}`}>
                            <button onClick={() => openDetail(req)} className="w-full text-left p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors">
                                <EnvelopeSimple size={18} weight="fill" className="text-gray-400 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-semibold text-sm">{req.name}</span>
                                        <span className="text-xs text-gray-400">{req.email}</span>
                                        <StatusBadge status={req.status} />
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                        {TYPE_LABEL[req.request_type] || req.request_type} · {req.created_at?.slice(0, 16).replace("T", " ")}
                                    </div>
                                </div>
                                <CaretDown size={16} className={`shrink-0 text-gray-400 transition-transform ${openId === req.id ? "rotate-180" : ""}`} />
                            </button>

                            {openId === req.id && (
                                <div className="border-t border-gray-100 p-4 space-y-4" data-testid="gdpr-request-detail">
                                    <div>
                                        <div className="overline mb-1">Messaggio</div>
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{req.message}</p>
                                    </div>

                                    {/* History */}
                                    <div>
                                        <div className="overline mb-2">Cronologia</div>
                                        <ul className="space-y-1.5">
                                            {(req.history || []).map((h, i) => (
                                                <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                                                    <Clock size={13} className="mt-0.5 text-gray-400 shrink-0" />
                                                    <span>
                                                        <StatusBadge status={h.status} />{" "}
                                                        {h.note && <span>— {h.note} </span>}
                                                        <span className="text-gray-400">· {h.by} · {h.at?.slice(0, 16).replace("T", " ")}</span>
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Update */}
                                    <div className="grid sm:grid-cols-[200px_1fr] gap-3 items-start pt-2 border-t border-gray-100">
                                        <Select value={draft.status} onValueChange={(v) => setDraft({ ...draft, status: v })}>
                                            <SelectTrigger className="h-10" data-testid="gdpr-status-select"><SelectValue placeholder="Stato" /></SelectTrigger>
                                            <SelectContent>
                                                {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        <Textarea
                                            rows={2}
                                            value={draft.note}
                                            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                                            placeholder="Nota interna (opzionale)…"
                                            data-testid="gdpr-status-note"
                                        />
                                    </div>
                                    <div className="flex justify-end">
                                        <button
                                            onClick={() => saveStatus(req.id)}
                                            disabled={saving}
                                            data-testid="gdpr-status-save"
                                            className="h-10 px-5 bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 transition-colors"
                                        >
                                            {saving ? "Salvataggio…" : "Aggiorna stato"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </Layout>
    );
}
